import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  rem,
} from '@mantine/core';
import dayjs from 'dayjs';
import { PiCopySimpleLight, PiWarning } from 'react-icons/pi';
import Loader from '@/components/Loader';
import KYCDocuments from '@/components/contacts/KYCDocuments';
import { KYCInfo } from '@/components/contacts/types';
import {
  useContact,
  useGenerateContactKycLink,
  useMondayContactKyc,
} from '@/models/contacts';

export default function MondayContactKycPanel({
  pulseId,
}: {
  pulseId: string;
}) {
  const monday = useContact(pulseId);
  const kyc = useMondayContactKyc(pulseId);
  const generate = useGenerateContactKycLink();

  if (monday.isLoading || kyc.isLoading) return <Loader />;

  const mondayContact = monday.data;
  const kycInfo = kyc.data?.info?.[0] ?? null;
  const documents = kyc.data?.documents ?? null;

  const email = mondayContact?.column_values?.find(
    (v) => v.column.id === 'contact_email',
  )?.text;
  const phone = mondayContact?.column_values?.find(
    (v) => v.column.id === 'contact_phone',
  )?.text;

  const title = mondayContact?.name || `Monday contact #${pulseId}`;

  return (
    <Paper p="md" h="100%">
      <Stack maw={960} gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Title order={3}>{title}</Title>
              <Badge variant="light" color="gray">
                Legacy Monday contact
              </Badge>
            </Group>
            <Group gap="sm">
              {email && (
                <Anchor href={`mailto:${email}`} size="sm">
                  {email}
                </Anchor>
              )}
              {phone && (
                <Text size="sm" c="dimmed">
                  {phone}
                </Text>
              )}
            </Group>
          </Stack>
        </Group>

        {!mondayContact && (
          <Alert color="yellow" icon={<PiWarning />}>
            <Box>
              Couldn't load this contact from Monday. You can still try to
              generate a SumSub link — if the pulse exists, it will work.
            </Box>
          </Alert>
        )}

        <Paper withBorder p="md">
          <Stack>
            <Text fw={600}>SumSub WebURL</Text>
            <TextInput
              variant="filled"
              readOnly
              value={kycInfo?.sumsubUrl ?? 'Not generated'}
              disabled={!kycInfo?.sumsubUrl}
              description={
                kycInfo?.sumsubUrlExpiresAt
                  ? `Expires ${dayjs(kycInfo.sumsubUrlExpiresAt).format(
                      'DD.MM.YYYY HH:mm',
                    )}`
                  : null
              }
              rightSection={
                kycInfo?.sumsubUrl && (
                  <CopyButton value={kycInfo.sumsubUrl} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? 'Copied' : 'Copy'}
                        withArrow
                        position="left"
                      >
                        <ActionIcon
                          color={copied ? 'teal' : 'gray'}
                          variant="subtle"
                          onClick={copy}
                        >
                          <PiCopySimpleLight style={{ width: rem(16) }} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                )
              }
            />
            <Group justify="flex-end">
              <Button
                size="xs"
                loading={generate.isPending}
                onClick={() => generate.mutate(pulseId)}
              >
                {kycInfo?.sumsubUrl ? 'Regenerate' : 'Generate'}
              </Button>
            </Group>
            {generate.isError && (
              <Text size="xs" c="red">
                Failed to generate link. The Monday pulse may not exist.
              </Text>
            )}
          </Stack>
        </Paper>

        {kycInfo && (
          <Paper withBorder p="md">
            <KYCDocuments
              kycInfo={kycInfo as unknown as KYCInfo}
              documents={documents}
            />
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
